import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

export default function AddReleaseDialog({ open, onOpenChange, onCreated }) {
  const [title, setTitle] = useState("");
  const [releaseDate, setReleaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [source, setSource] = useState("spotify");
  const [spotifyUrl, setSpotifyUrl] = useState("");
  const [cover, setCover] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setTitle(""); setReleaseDate(new Date().toISOString().slice(0, 10));
    setSource("spotify"); setSpotifyUrl(""); setCover(null); setPreview(null);
  };

  const pick = (f) => {
    if (!f) return;
    setCover(f);
    const r = new FileReader();
    r.onload = (e) => setPreview(e.target.result);
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title required");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("release_date", releaseDate);
      fd.append("source", source);
      if (spotifyUrl) fd.append("spotify_url", spotifyUrl);
      if (cover) fd.append("cover", cover);
      await api.post("/releases", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Release added.");
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to add release";
      toast.error(typeof detail === "string" ? detail : "Failed to add release");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 rounded-sm max-w-xl text-white max-h-[92vh] overflow-y-auto"
        data-testid="add-release-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tight">Add release</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 mt-2">
          <div>
            <div className="label-mono mb-2">Cover art</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => pick(e.target.files?.[0])}
              data-testid="add-release-file-input"
            />
            {preview ? (
              <div className="relative group w-36 h-36">
                <img src={preview} alt="Cover preview" className="w-full h-full object-cover border border-white/10" />
                <button
                  type="button"
                  className="absolute top-1 right-1 bg-black/80 border border-white/20 p-1"
                  onClick={() => { setCover(null); setPreview(null); if (fileRef.current) fileRef.current.value = ""; }}
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-36 h-36 border border-dashed border-white/20 flex flex-col items-center justify-center gap-1 text-white/55 hover:text-white hover:border-[#FF3B00] transition-colors"
                data-testid="add-release-upload-btn"
              >
                <Upload size={20} strokeWidth={1.2} />
                <div className="label-mono">Cover</div>
              </button>
            )}
          </div>
          <div>
            <div className="label-mono mb-2">Title</div>
            <input
              className="mp-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Obsidian Pulse"
              required
              data-testid="add-release-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <div className="label-mono mb-2">Release date</div>
              <input
                type="date"
                className="mp-input"
                value={releaseDate}
                onChange={(e) => setReleaseDate(e.target.value)}
                data-testid="add-release-date"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Source</div>
              <select
                className="mp-input bg-[#0A0A0A]"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                data-testid="add-release-source"
              >
                <option value="spotify">Spotify</option>
                <option value="soundcloud">SoundCloud</option>
                <option value="apple_music">Apple Music</option>
              </select>
            </div>
          </div>
          <div>
            <div className="label-mono mb-2">Link (optional)</div>
            <input
              type="url"
              className="mp-input"
              value={spotifyUrl}
              onChange={(e) => setSpotifyUrl(e.target.value)}
              placeholder="https://open.spotify.com/track/..."
              data-testid="add-release-url"
            />
          </div>
        </form>

        <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-white/10">
          <button className="btn-ghost-line" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</button>
          <button
            className="btn-amber disabled:opacity-60"
            onClick={submit}
            disabled={submitting}
            data-testid="add-release-submit"
          >
            {submitting ? "Saving…" : "Add release"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
